import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../Lib/Firebase";

type MessageTimestamp = { toDate?: () => Date; toMillis?: () => number } | string | Date | null;

export type StudentMessage = {
    id: string;
    recipientUid: string;
    source: "SYSTEM" | "ADMIN";
    category: string;
    title: string;
    body: string;
    createdAt: MessageTimestamp;
    readAt: MessageTimestamp;
    action?: { label?: string; href?: string };
    details?: {
        kind?: "COURSE" | "INTERVIEW";
        courseTitle?: string;
        runTitle?: string;
        itemTitle?: string;
        targetRole?: string;
        startAt?: MessageTimestamp;
        endAt?: MessageTimestamp;
        timezone?: string;
        paymentStatus?: string;
        amountMinor?: number;
        currency?: string;
        paymentReference?: string;
    };
};

function timestampMillis(value: MessageTimestamp) {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "string") {
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === "object" && "toMillis" in value && typeof value.toMillis === "function") return value.toMillis();
    if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") return value.toDate().getTime();
    return 0;
}

export default function useStudentMessages(uid?: string) {
    const [messages, setMessages] = useState<StudentMessage[]>([]);
    const [loading, setLoading] = useState(Boolean(uid));
    const [error, setError] = useState("");

    useEffect(() => {
        if (!uid) { setMessages([]); setLoading(false); setError(""); return; }
        setLoading(true);
        const messagesQuery = query(collection(db, "studentMessages"), where("recipientUid", "==", uid));
        return onSnapshot(messagesQuery, (snapshot) => {
            const next = snapshot.docs
                .map((message) => ({ id: message.id, ...message.data() } as StudentMessage))
                .sort((left, right) => timestampMillis(right.createdAt) - timestampMillis(left.createdAt));
            setMessages(next);
            setLoading(false);
            setError("");
        }, () => {
            setLoading(false);
            setError("Your messages could not be loaded. Please try again.");
        });
    }, [uid]);

    const unreadCount = useMemo(() => messages.filter((message) => !message.readAt).length, [messages]);
    const setReadState = async (messageId: string, read: boolean) => {
        try {
            await updateDoc(doc(db, "studentMessages", messageId), { readAt: read ? serverTimestamp() : null });
        } catch {
            setError("The message status could not be updated. Please try again.");
        }
    };

    return { messages, unreadCount, loading, error, setReadState };
}
